import { formatMoney } from 'lib/functions/utils';

import {
  CalculatedLine,
  DiffLine,
  DiffResult,
  SavedEntry,
  SavedEntryLine,
} from './types';

type LineSide = 'debit' | 'credit' | 'mixed';
const DIFF_HIGHLIGHT_THRESHOLD = 1;

interface LinePair {
  savedLine?: SavedEntryLine;
  calculatedLine?: CalculatedLine;
}

const normalizeAmount = (amount: number): number =>
  Math.round(amount * 100) / 100 || 0;

const getSavedBaseKey = (line: SavedEntryLine): string =>
  `${line.node?.accountCode || ''}_${line.documentKey || ''}`;

const getCalculatedBaseKey = (line: CalculatedLine): string =>
  `${line.account || ''}_${line.documentId || ''}`;

const getSavedSide = (line: SavedEntryLine): LineSide => {
  const debit = normalizeAmount(line.debit);
  const credit = normalizeAmount(line.credit);

  if (debit !== 0 && credit === 0) return 'debit';
  if (credit !== 0 && debit === 0) return 'credit';
  return 'mixed';
};

const getCalculatedSide = (line: CalculatedLine): LineSide => {
  const debit = normalizeAmount(line.debit);
  const credit = normalizeAmount(line.credit);

  if (debit !== 0 && credit === 0) return 'debit';
  if (credit !== 0 && debit === 0) return 'credit';
  return 'mixed';
};

const getSavedExactMatchKey = (line: SavedEntryLine): string => {
  const side = getSavedSide(line);

  if (side === 'debit') {
    return `${side}_${normalizeAmount(line.debit).toFixed(2)}`;
  }

  if (side === 'credit') {
    return `${side}_${normalizeAmount(line.credit).toFixed(2)}`;
  }

  return `${side}_${normalizeAmount(line.debit).toFixed(2)}_${normalizeAmount(line.credit).toFixed(2)}`;
};

const getCalculatedExactMatchKey = (line: CalculatedLine): string => {
  const side = getCalculatedSide(line);

  if (side === 'debit') {
    return `${side}_${normalizeAmount(line.debit).toFixed(2)}`;
  }

  if (side === 'credit') {
    return `${side}_${normalizeAmount(line.credit).toFixed(2)}`;
  }

  return `${side}_${normalizeAmount(line.debit).toFixed(2)}_${normalizeAmount(line.credit).toFixed(2)}`;
};

const buildLinePairs = (
  savedLines: SavedEntryLine[],
  calculatedLines: CalculatedLine[],
): LinePair[] => {
  const pairs: LinePair[] = [];
  const usedSavedIndexes = new Set<number>();
  const usedCalculatedIndexes = new Set<number>();

  savedLines.forEach((savedLine, savedIndex) => {
    const savedMatchKey = getSavedExactMatchKey(savedLine);
    const matchedCalculatedIndex = calculatedLines.findIndex(
      (calculatedLine, calculatedIndex) =>
        !usedCalculatedIndexes.has(calculatedIndex) &&
        getCalculatedExactMatchKey(calculatedLine) === savedMatchKey,
    );

    if (matchedCalculatedIndex === -1) return;

    usedSavedIndexes.add(savedIndex);
    usedCalculatedIndexes.add(matchedCalculatedIndex);
    pairs.push({
      savedLine,
      calculatedLine: calculatedLines[matchedCalculatedIndex],
    });
  });

  savedLines.forEach((savedLine, savedIndex) => {
    if (usedSavedIndexes.has(savedIndex)) return;

    const savedSide = getSavedSide(savedLine);
    const matchedCalculatedIndex = calculatedLines.findIndex(
      (calculatedLine, calculatedIndex) =>
        !usedCalculatedIndexes.has(calculatedIndex) &&
        getCalculatedSide(calculatedLine) === savedSide,
    );

    if (matchedCalculatedIndex === -1) return;

    usedSavedIndexes.add(savedIndex);
    usedCalculatedIndexes.add(matchedCalculatedIndex);
    pairs.push({
      savedLine,
      calculatedLine: calculatedLines[matchedCalculatedIndex],
    });
  });

  savedLines.forEach((savedLine, savedIndex) => {
    if (usedSavedIndexes.has(savedIndex)) return;
    pairs.push({ savedLine });
  });

  calculatedLines.forEach((calculatedLine, calculatedIndex) => {
    if (usedCalculatedIndexes.has(calculatedIndex)) return;
    pairs.push({ calculatedLine });
  });

  return pairs;
};

export const generateDiff = (
  savedEntry: SavedEntry,
  calculatedLines: CalculatedLine[],
): DiffResult => {
  const diffLines: DiffLine[] = [];

  const savedMap = new Map<string, SavedEntryLine[]>();
  const calculatedMap = new Map<string, CalculatedLine[]>();

  savedEntry.lines.forEach((line) => {
    const key = getSavedBaseKey(line);
    const bucket = savedMap.get(key) || [];
    bucket.push(line);
    savedMap.set(key, bucket);
  });

  calculatedLines.forEach((line) => {
    const key = getCalculatedBaseKey(line);
    const bucket = calculatedMap.get(key) || [];
    bucket.push(line);
    calculatedMap.set(key, bucket);
  });

  const allKeys = new Set([...savedMap.keys(), ...calculatedMap.keys()]);

  let totalDifferences = 0;

  allKeys.forEach((compositeKey) => {
    const savedBucket = savedMap.get(compositeKey) || [];
    const calculatedBucket = calculatedMap.get(compositeKey) || [];
    const linePairs = buildLinePairs(savedBucket, calculatedBucket);

    linePairs.forEach(({ savedLine, calculatedLine }) => {
      const savedDebit = savedLine?.debit ?? null;
      const savedCredit = savedLine?.credit ?? null;
      const calculatedDebit = calculatedLine?.debit ?? null;
      const calculatedCredit = calculatedLine?.credit ?? null;

      const rawDebitDiff = (calculatedDebit ?? 0) - (savedDebit ?? 0);
      const rawCreditDiff = (calculatedCredit ?? 0) - (savedCredit ?? 0);
      const debitDiff = Math.round(rawDebitDiff * 100) / 100 || 0;
      const creditDiff = Math.round(rawCreditDiff * 100) / 100 || 0;

      const hasDifference =
        Math.abs(debitDiff) > DIFF_HIGHLIGHT_THRESHOLD ||
        Math.abs(creditDiff) > DIFF_HIGHLIGHT_THRESHOLD;

      if (hasDifference) totalDifferences++;

      diffLines.push({
        accountCode:
          savedLine?.node?.accountCode || calculatedLine?.account || '',
        accountName: savedLine?.node?.key || calculatedLine?.accountName || '',
        documentKey: savedLine?.documentKey || calculatedLine?.documentId || '',
        savedDebit,
        savedCredit,
        calculatedDebit,
        calculatedCredit,
        hasDifference,
        debitDiff,
        creditDiff,
      });
    });
  });

  diffLines.sort((a, b) => {
    const codeCompare = a.accountCode.localeCompare(b.accountCode);
    if (codeCompare !== 0) return codeCompare;
    return a.documentKey.localeCompare(b.documentKey);
  });

  const savedTotalDebit = savedEntry.lines.reduce((sum, l) => sum + l.debit, 0);
  const savedTotalCredit = savedEntry.lines.reduce(
    (sum, l) => sum + l.credit,
    0,
  );
  const calculatedTotalDebit = calculatedLines.reduce(
    (sum, l) => sum + l.debit,
    0,
  );
  const calculatedTotalCredit = calculatedLines.reduce(
    (sum, l) => sum + l.credit,
    0,
  );

  const totalDebitDiff =
    Math.round((calculatedTotalDebit - savedTotalDebit) * 100) / 100 || 0;
  const totalCreditDiff =
    Math.round((calculatedTotalCredit - savedTotalCredit) * 100) / 100 || 0;

  return {
    lines: diffLines,
    totalDifferences,
    savedTotalDebit,
    savedTotalCredit,
    calculatedTotalDebit,
    calculatedTotalCredit,
    debitDiff: totalDebitDiff,
    creditDiff: totalCreditDiff,
  };
};

export const formatDiff = (diff: number): string => {
  if (diff === 0) return '';
  const sign = diff > 0 ? '+' : '';
  return `(${sign}${formatMoney(diff)})`;
};
